/**
 * Authentication Context
 *
 * Manages user authentication state, session management, and profile data.
 * The Supabase Auth UI widget handles sign in/up/OAuth directly;
 * this context listens for auth state changes and manages profiles.
 *
 * Beta access: new signups must have their email in the beta_whitelist table.
 * Non-whitelisted users are signed out automatically.
 */

import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { AuthContextValue, UserProfile } from '../types/auth.types';

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to prevent redundant profile fetches on tab refocus
  const profileRef = useRef<UserProfile | null>(null);

  // Check if email is in the beta whitelist
  const checkWhitelist = useCallback(async (email: string): Promise<boolean> => {
    try {
      const { data, error: whitelistError } = await supabase
        .from('beta_whitelist')
        .select('email')
        .eq('email', email.toLowerCase())
        .single();

      if (whitelistError || !data) return false;
      return true;
    } catch {
      return false;
    }
  }, []);

  // Fetch user profile from database
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return null;
      }

      return data as UserProfile;
    } catch (err) {
      console.error('Unexpected error fetching profile:', err);
      return null;
    }
  }, []);

  // Load profile for authenticated user — creates one if it doesn't exist
  const loadProfile = useCallback(async (authUser: User) => {
    setIsLoadingProfile(true);
    try {
      let profileData = await fetchProfile(authUser.id);

      // Auto-create profile if it doesn't exist (first sign-in)
      if (!profileData && authUser.email) {
        const { error: createError } = await supabase
          .from('user_profiles')
          .upsert({
            id: authUser.id,
            email: authUser.email,
          }, { onConflict: 'id' });

        if (createError) {
          console.error('Failed to create profile:', createError);
        } else {
          profileData = await fetchProfile(authUser.id);
        }
      }

      setProfile(profileData);
      profileRef.current = profileData;
    } finally {
      setIsLoadingProfile(false);
    }
  }, [fetchProfile]);

  // Listen for auth state changes
  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return;
        console.log('[AuthContext] onAuthStateChange:', event, currentSession?.user?.email ?? 'no user');

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (event === 'INITIAL_SESSION') {
          if (currentSession?.user) {
            await loadProfile(currentSession.user);
          }
          setIsLoading(false);
        } else if (event === 'SIGNED_IN' && currentSession?.user) {
          const authUser = currentSession.user;

          // Beta whitelist check — reject non-whitelisted users
          if (authUser.email) {
            const isWhitelisted = await checkWhitelist(authUser.email);
            if (!isWhitelisted) {
              setError('This email is not registered for beta access. Please contact the game creator.');
              await supabase.auth.signOut();
              return;
            }
          }

          // Only fetch profile if we don't already have one
          if (!profileRef.current) {
            await loadProfile(authUser);
          }
          setIsLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          profileRef.current = null;
          setIsLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile, checkWhitelist]);

  // Sign out — clear local state immediately, then tell Supabase.
  // Don't await Supabase signOut as it can hang due to lock contention.
  const signOut = useCallback(async () => {
    console.log('[AuthContext] signOut called');
    setError(null);
    // Clear local state immediately so the UI responds
    setUser(null);
    setSession(null);
    setProfile(null);
    profileRef.current = null;
    // Tell Supabase to clear the session (fire-and-forget)
    supabase.auth.signOut().catch(err => {
      console.error('[AuthContext] supabase.auth.signOut error:', err);
    });
  }, []);

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('No user signed in');

    setIsLoadingProfile(true);
    try {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;
      await loadProfile(user);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      setError(message);
      throw err;
    } finally {
      setIsLoadingProfile(false);
    }
  }, [user, loadProfile]);

  // Refresh profile from database
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await loadProfile(user);
  }, [user, loadProfile]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextValue = {
    user,
    session,
    profile,
    isLoading,
    isLoadingProfile,
    error,
    signOut,
    updateProfile,
    refreshProfile,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
